// @flow
import React from 'react'
import I18n from 'i18n-react'
import Modal from 'reactstrap/lib/Modal'
import ModalBody from 'reactstrap/lib/ModalBody'
import ModalHeader from 'reactstrap/lib/ModalHeader'
import Immutable from 'seamless-immutable'
import Loadable from 'react-loadable'

const VehicleInfoSubmodule = Loadable({
  loader: () => import('../../submodules/vehicleInfo/src'),
  loading() {
    return <span />
  },
})

// types
import type { DefaultMiniAppProps } from './'
import { checkStateIfNeedToUpdate } from '../../Core/Components/_formFields/AbstractFormField'

type VehiclePriceAppProps = DefaultMiniAppProps & {
  miniAppData: {
    addGtmEvent: (action: string, label: string, value: string) => void,
  },
}

type VehiclePriceAppState = {
  modalOpened: boolean,
  setDataCl?: Function,
}

const FORM_DATA_CHANGING_IN = [
  ['vin'],
  ['type'],
  ['regPlate'],
  ['policyHolder', 'phoneNumber'],
  ['mileage'],
  ['firstRegistered'],
]

export default class VehiclePriceApp extends React.Component<VehiclePriceAppProps, VehiclePriceAppState> {
  static defaultProps = {
    isBackOffice: false,
  }

  state = {
    modalOpened: false,
    setDataCl: undefined,
  }

  shouldComponentUpdate(nextProps: VehiclePriceAppProps, nextState: VehiclePriceAppState) {
    return (
      JSON.stringify(this.props.activeSettings) !== JSON.stringify(nextProps.activeSettings) ||
      JSON.stringify(this.props.resources) !== JSON.stringify(nextProps.resources) ||
      FORM_DATA_CHANGING_IN.reduce((acc, inData) => {
        return acc || this.props.formData.getIn(inData) !== nextProps.formData.getIn(inData)
      }, false) ||
      checkStateIfNeedToUpdate(nextState, this.state)
    )
  }

  componentDidUpdate(prevProps: VehiclePriceAppProps) {
    const { formData, isBackOffice } = this.props
    const { setDataCl } = this.state
    if (typeof setDataCl === 'function') {
      const data = {}
      if (formData.getIn(['vin'], null) !== prevProps.formData.getIn(['vin'], null)) {
        data.modalVIN = formData.getIn(['vin'], null)
      }
      if (formData.getIn(['regPlate'], null) !== prevProps.formData.getIn(['regPlate'], null)) {
        data.modalRZ = formData.getIn(['regPlate'], null)
      }
      if (
        !isBackOffice &&
        formData.getIn(['policyHolder', 'phoneNumber'], null) !==
          prevProps.formData.getIn(['policyHolder', 'phoneNumber'], null)
      ) {
        data.modalPhone = formData.getIn(['policyHolder', 'phoneNumber'], null)
      }
      if (formData.getIn(['mileage'], null) !== prevProps.formData.getIn(['mileage'], null)) {
        data.modalMileage = formData.getIn(['mileage'], null)
      }
      if (formData.getIn(['verificationCode'], null) !== prevProps.formData.getIn(['verificationCode'], null)) {
        data.verificationCode = formData.getIn(['verificationCode'], null)
      }
      if (formData.getIn(['firstRegistered'], null) !== prevProps.formData.getIn(['firstRegistered'], null)) {
        data.firstRegistered = formData.getIn(['firstRegistered'], null)
      }
      setDataCl(data)
    }
  }

  handleOpenModal = () => {
    this.setState({ modalOpened: true })
    if (typeof this.state.setDataCl === 'function') {
      this.handleSetDefaultData(this.state.setDataCl)
    }
    if (!this.props.isBackOffice) {
      this.props.miniAppData.addGtmEvent('Activation', 'Cena vozidla')
    }
  }

  handleCloseModal = () => {
    this.setState({ modalOpened: false })
  }

  handleCreate = (setDataCl: Function) => {
    this.handleSetDefaultData(setDataCl)
  }

  handleSetDefaultData = (setDataCl: Function) => {
    if (typeof setDataCl === 'function') {
      this.setState({ setDataCl })
      const userData = {
        modalVIN: this.props.formData.getIn(['vin'], null),
        modalRZ: this.props.formData.getIn(['regPlate'], null),
        modalPhone: this.props.formData.getIn(['policyHolder', 'phoneNumber'], null),
        modalMileage: this.props.formData.getIn(['mileage'], null),
        verificationCode: this.props.formData.getIn(['verificationCode'], null),
        firstRegistered: this.props.formData.getIn(['firstRegistered'], null),
      }
      const modalData = {}
      Object.keys(userData).map((key) => {
        const value = userData[key]
        if (value) {
          modalData[key] = value
        }
      })
      setDataCl(modalData)
    }
  }

  handleGetPrice = () => {
    if (!this.props.isBackOffice) {
      this.props.miniAppData.addGtmEvent('Send', 'Cena vozidla')
    }
  }

  handleFinish = (finished: boolean, data: Object, selected: number, withVat: boolean = false) => {
    const { onChangeFormData, isBackOffice } = this.props
    let formData = new Immutable({})
    if (finished && data.prices && selected) {
      this.setState({ modalOpened: false })
      const price = Math.round(data.prices[selected] * (withVat ? 1.21 : 1))
      if (data.modalPhone) {
        formData = formData.setIn(['policyHolder', 'phoneNumber'], data.modalPhone)
      }
      formData = formData
        .setIn(['cic', 'carValue'], price)
        .setIn(['mileage'], data.modalMileage)
        .setIn(['cicEnabled'], true)
        .setIn(['cic', 'priceExcludesVAT'], !withVat)
      onChangeFormData(formData, ['cic', 'carValue', 'mileage'])
    } else if (typeof data.valid === 'undefined') {
      const changed = []
      if (!isBackOffice && typeof data.modalPhone === 'string') {
        changed.push('policyHolder')
        changed.push('phoneNumber')
        formData = formData.setIn(['policyHolder', 'phoneNumber'], data.modalPhone)
      }
      if (typeof data.modalRZ === 'string') {
        changed.push('regPlate')
        formData = formData.setIn(['regPlate'], data.modalRZ)
      }
      if (typeof data.modalMileage === 'string' || typeof data.modalMileage === 'number') {
        changed.push('mileage')
        formData = formData.setIn(['mileage'], data.modalMileage)
      }
      if (typeof data.modalVIN === 'string') {
        changed.push('vin')
        formData = formData.setIn(['vin'], data.modalVIN)
      }
      onChangeFormData(formData, changed)
    } else if (typeof this.props.miniAppData.onChangeValidationError === 'function') {
      if (!data.valid.modalMileage) {
        this.props.miniAppData.onChangeValidationError(['mileage'], { error_type: 'required_field_missing' })
      }
    }
  }

  handleRenderBottomContent = () => (
    <span>
      {this.props.activeSettings &&
        this.props.activeSettings.domain &&
        I18n.translate('general.agreeWithTermsAndConditions', {
          link: (
            <a
              href={`${this.props.activeSettings.domain}informace-o-zpracovani-osobnich-udaju/`}
              rel="noopener noreferrer"
              target="_blank"
            >
              {I18n.translate('general.infoAboutDataProcessing')}
            </a>
          ),
        })}
    </span>
  )

  handleSetVerificationCode = (verificationCode: string) => {
    this.props.onChangeFormData({ verificationCode }, ['verificationCode'])
  }

  render() {
    const { isBackOffice, miniAppData, formData } = this.props
    let showMileagePush = false
    if (miniAppData && miniAppData.showMileagePush) {
      showMileagePush = true
    }

    if (!['car', 'van'].includes(formData.getIn(['type'], 'car'))) {
      return null
    }

    return [
      !isBackOffice ? (
        <div className="bg-active-object rounded" key="vehicle-price-mini-app-content">
          <div className="row align-items-center p-3 my-4">
            <div className="col-12 col-md-8">
              <p className="lead mb-0">{I18n.translate('miniApps.vehicleInfo.priceInfo')}</p>
            </div>
            <div className="col-12 col-md-4 pl-lg-0">
              <button className="btn btn-dark w-100" onClick={this.handleOpenModal} type="button">
                {I18n.translate('miniApps.vehicleInfo.getPrice')}
              </button>
            </div>
          </div>
        </div>
      ) : (
        <VehicleInfoSubmodule
          isBackOffice={isBackOffice}
          isCarValuationView
          key="vehicle-price-mini-app-miniApp"
          onCreate={this.handleCreate}
          onFinish={this.handleFinish}
          onGetPrice={this.handleGetPrice}
          onSetVerificationCode={this.handleSetVerificationCode}
          showMileagePush={showMileagePush}
        />
      ),
      !isBackOffice ? (
        <Modal
          className="modal-dialog modal-lg h-100 h-sm-auto mx-0 my-0 mx-sm-auto my-sm-2"
          contentClassName="modal-content h-100 h-sm-auto border-0 border-sm-primary"
          isOpen={this.state.modalOpened}
          key="vehicle-price-mini-app-modal"
          toggle={this.handleCloseModal}
        >
          <ModalHeader>
            <button className="close" onClick={this.handleCloseModal} type="button">
              <i className="pfp pfp-close" />
            </button>
          </ModalHeader>
          <ModalBody className="modal-body d-flex d-sm-block align-items-center">
            <VehicleInfoSubmodule
              isBackOffice={isBackOffice}
              isCarValuationView
              onCreate={this.handleCreate}
              onFinish={this.handleFinish}
              onGetPrice={this.handleGetPrice}
              onSetVerificationCode={this.handleSetVerificationCode}
              renderBottomContent={this.handleRenderBottomContent}
              showMileagePush={showMileagePush}
            />
          </ModalBody>
        </Modal>
      ) : null,
    ]
  }
}
